import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProcedureDialog from "@/components/ProcedureDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Search, Plus, MoreHorizontal, Eye, Pencil, Trash2, Copy, History } from "lucide-react";
import { toast } from "sonner";

export default function Procedures() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [procs, setProcs] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => {
    api.get("/procedures").then((r) => setProcs(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(load, []);

  const filtered = useMemo(() => procs.filter((p) => {
    const mq = p.name.toLowerCase().includes(q.toLowerCase()) || (p.tags || []).join(" ").toLowerCase().includes(q.toLowerCase());
    const mc = cat === "all" || p.category === cat;
    const ms = status === "all" || p.status === status;
    return mq && mc && ms;
  }), [procs, q, cat, status]);

  const del = async () => {
    try {
      await api.delete(`/procedures/${delTarget.id}`);
      toast.success("Procedure deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const duplicate = async (p) => {
    try { await api.post(`/procedures/${p.id}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="procedures-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Procedures</h2>
          <p className="text-slate-500 mt-1">{filtered.length} of {procs.length} procedures</p>
        </div>
        {isAdmin && <Button onClick={() => { setEditing(null); setDialog(true); }} data-testid="add-procedure-btn" className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><Plus className="h-4 w-4" /> Add Procedure</Button>}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by name or tag…" data-testid="search-procedures" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[190px] h-11" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[150px] h-11" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
        </Select>
      </div>

      <Card className="rounded-xl border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
                <TableHead>Procedure</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Avg</TableHead>
                <TableHead className="text-right">Max</TableHead>
                <TableHead className="text-right">Offer</TableHead>
                <TableHead className="text-right">Comm %</TableHead>
                <TableHead className="text-right">Profit %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`proc-row-${p.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <TableCell>
                    <button onClick={() => navigate(`/procedures/${p.id}`)} className="flex items-center gap-3 text-left">
                      <img src={imgUrl(p.cover_image)} alt="" className="h-10 w-10 rounded-lg object-cover bg-slate-100" />
                      <div>
                        <div className="font-medium flex items-center gap-2">{p.name}{p.featured && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Popular</Badge>}</div>
                        <div className="text-xs text-slate-400">Updated {(p.modified_date || "").slice(0, 10)}</div>
                      </div>
                    </button>
                  </TableCell>
                  <TableCell><span className="text-sm text-slate-600 dark:text-slate-300">{p.category}</span></TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{fmtINR(p.min_price)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-medium">{fmtINR(p.avg_price)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm">{fmtINR(p.max_price)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-teal-600">{fmtINR(p.offer_price)}</TableCell>
                  <TableCell className="text-right text-sm">{p.commission_pct}%</TableCell>
                  <TableCell className="text-right text-sm"><span className={p.profit_pct < 20 ? "text-red-600 font-medium" : "text-emerald-600"}>{p.profit_pct}%</span></TableCell>
                  <TableCell><Badge variant="outline" className={p.status === "Active" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-500"}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`actions-${p.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900">
                        <DropdownMenuItem onClick={() => navigate(`/procedures/${p.id}`)}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        {isAdmin && <DropdownMenuItem onClick={() => { setEditing(p); setDialog(true); }} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                        {isAdmin && <DropdownMenuItem onClick={() => duplicate(p)}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => navigate(`/procedures/${p.id}?tab=history`)}><History className="h-4 w-4 mr-2" /> History</DropdownMenuItem>
                        {isAdmin && <DropdownMenuItem onClick={() => setDelTarget(p)} className="text-red-600" data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">No procedures found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ProcedureDialog open={dialog} onOpenChange={setDialog} procedure={editing} categories={cats} onSaved={load} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-white dark:bg-slate-900">
          <AlertDialogHeader><AlertDialogTitle>Delete {delTarget?.name}?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="confirm-delete" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
